import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  CheckCircle,
  ArrowRight,
  Shield,
  ClipboardCheck,
  Users,
  Settings,
  Rocket,
  Clock,
  CalendarCheck,
  FileSearch,
  FileText,
  Wrench,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Sparkles,
  ArrowUpRight,
  Check,
  Phone,
  Loader2,
  Building2,
  TrendingUp,
  Target,
  Zap,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import AnimateOnScroll from "../components/AnimateOnScroll";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const CALENDLY_URL = "https://calendly.com/rnb-consulting-group/free-discovery-call";

const IMG_HERO = "https://customer-assets.emergentagent.com/job_7865eaef-d264-4504-a997-7bd20f6ab0e8/artifacts/3tsoplu6_doctor-working-in-hospital-use-calculator-healthc-2024-12-19-15-02-23-utc-1024x683.jpg";
const IMG_TEAM = "https://customer-assets.emergentagent.com/job_7865eaef-d264-4504-a997-7bd20f6ab0e8/artifacts/dxdgzst9_image-compliance-surveyors-webinar-cta.webp";
const IMG_WALKTHRU = "https://customer-assets.emergentagent.com/job_7865eaef-d264-4504-a997-7bd20f6ab0e8/artifacts/803yy1gm_healthcareaudit5-560x340.webp";

const HealthcareOperationalAudits = () => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", organization: "", role: "", main_challenge: "", employee_count: "", timeline: "", org_structure: "", challenge: "" });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.organization || !formData.phone || !formData.main_challenge || !formData.employee_count || !formData.timeline || !formData.org_structure) return;
    setFormSubmitting(true);
    try {
      const r = await fetch(`${API}/api/audit-inquiry/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (r.ok) {
        setFormSubmitted(true);
        toast.success("Request submitted! Check your email for confirmation.");
      } else {
        const d = await r.json();
        toast.error(d.detail || "Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const auditServices = [
    { icon: Shield, title: "Survey & Accreditation Readiness", outcome: "Go into your next survey confident, not scrambling", items: ["Policy & procedure audit", "Training gap analysis", "Credentialing review", "Accountability mapping"], gradient: "from-blue-500 to-cyan-500" },
    { icon: Settings, title: "Workflow & Staffing Optimization", outcome: "Eliminate bottlenecks that drain your team daily", items: ["Scheduling & coverage analysis", "Task ownership clarity", "Communication flow mapping", "Operational strain reduction"], gradient: "from-purple-500 to-pink-500" },
    { icon: ClipboardCheck, title: "Practice Operations Audit", outcome: "Bring order when you've outgrown your systems", items: ["End-to-end workflow evaluation", "Process documentation", "Administrative restructuring", "Risk identification"], gradient: "from-orange-500 to-red-500" },
    { icon: Rocket, title: "Launch & Expansion Readiness", outcome: "Open new locations without operational chaos", items: ["Pre-launch readiness review", "Staffing structure planning", "Training rollout plan", "Risk mitigation"], gradient: "from-teal-500 to-emerald-500" },
    { icon: Users, title: "Workforce & PTO Structure Audit", outcome: "See the real cost of coverage gaps and burnout", items: ["PTO accrual logic review", "Absence coverage planning", "Workforce cost analysis", "Burnout risk indicators"], gradient: "from-indigo-500 to-blue-500" },
  ];

  const faqs = [
    { q: "How long does a typical audit take?", a: "Most engagements are completed within 3–6 weeks depending on scope. We work on your schedule to minimize disruption." },
    { q: "Do we need to prepare anything beforehand?", a: "We'll provide a simple checklist. Most organizations already have what's needed. we handle the heavy lifting." },
    { q: "Is there an obligation after the initial consultation?", a: "None at all. The consultation helps us both determine if there's a fit. Many organizations find value just from that conversation." },
    { q: "What size organization do you typically work with?", a: "We work with healthcare organizations of all sizes. from single-site practices to multi-location health systems." },
    { q: "Can you help implement the recommendations?", a: "Yes. Step 4 of our process is optional implementation support. hands-on help with process redesign, training, and policy refinement." },
  ];

  return (
    <div className="overflow-hidden">
      <SEO
        title="Healthcare Operational Audits | RNB Consulting Group"
        description="Structured operational and compliance audits for growing healthcare organizations. Improve workflows, reduce risk, prepare for accreditation."
        keywords="healthcare operational audit, compliance audit, accreditation readiness, workflow optimization, healthcare staffing audit"
      />
      <Navbar />

      {/* ═══ HERO. Problem + Promise + CTA ═══ */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG_HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-rnb-navy/95 via-rnb-navy/92 to-rnb-blue/85" />
        </div>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-64 h-64 bg-rnb-teal/20 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-32 left-10 w-80 h-80 bg-rnb-blue/15 rounded-full blur-3xl animate-float" style={{ animationDelay: "3s" }} />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(rgba(31,198,183,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(31,198,183,0.4) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 w-full">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
              <AnimateOnScroll>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
                  <Sparkles className="w-4 h-4 text-rnb-teal" />
                  <span className="text-sm font-medium text-white/90">For Growing Healthcare Organizations</span>
                </div>
              </AnimateOnScroll>
              <AnimateOnScroll delay={0.1}>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-white tracking-tight leading-[1.08] mb-5" data-testid="hero-heading">
                  Bring Structure Back to Your Healthcare Operations
                </h1>
              </AnimateOnScroll>
              <AnimateOnScroll delay={0.15}>
                <p className="text-2xl sm:text-3xl font-bold text-rnb-teal leading-snug mb-6">
                  Before small issues become costly problems.
                </p>
              </AnimateOnScroll>
              <AnimateOnScroll delay={0.2}>
                <p className="text-base sm:text-lg text-white/60 leading-relaxed mb-8 max-w-xl">
                  Structured operational and compliance audits designed to improve workflows, reduce risk, and prepare your team for what&apos;s ahead.
                </p>
              </AnimateOnScroll>
              <AnimateOnScroll delay={0.25}>
                <div className="flex flex-wrap gap-3 mb-8">
                  <Button onClick={scrollToForm} size="lg" className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full px-8 h-13 text-base font-semibold shadow-lg shadow-rnb-teal/25 gap-2" data-testid="hero-cta-primary">
                    Request Your Free Audit Overview <ArrowRight className="w-4 h-4" />
                  </Button>
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="lg" className="text-white border-white/30 hover:bg-white/10 rounded-full px-6 h-13 text-base gap-2" data-testid="hero-cta-call">
                      <Phone className="w-4 h-4" /> Book a Call
                    </Button>
                  </a>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-white/40 text-xs">
                  <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-rnb-teal/60" /> No obligation</span>
                  <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-rnb-teal/60" /> Results in 3–6 weeks</span>
                  <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-rnb-teal/60" /> Healthcare-focused team</span>
                </div>
              </AnimateOnScroll>
            </div>

            {/* Hero-side stat callouts */}
            <div className="lg:col-span-2 hidden lg:flex flex-col gap-4">
              <AnimateOnScroll delay={0.3}>
                {[
                  { num: "85%", label: "of audit findings are actionable within 30 days", icon: Target },
                  { num: "3–6 wk", label: "typical engagement timeline", icon: Clock },
                  { num: "100%", label: "structured around your schedule", icon: CalendarCheck },
                ].map((s, i) => (
                  <div key={s.key || s.name || s.title || i} className="bg-white/[0.07] backdrop-blur-sm border border-white/10 rounded-xl p-5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-rnb-teal/15 flex items-center justify-center shrink-0">
                      <s.icon className="w-5 h-5 text-rnb-teal" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white font-heading">{s.num}</p>
                      <p className="text-xs text-white/50">{s.label}</p>
                    </div>
                  </div>
                ))}
              </AnimateOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PROBLEM AGITATION. "Is This You?" ═══ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <AnimateOnScroll direction="left">
              <span className="text-rnb-teal font-semibold text-sm uppercase tracking-wider">Sound Familiar?</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mt-2 mb-3">
                Is Your Organization Experiencing Any of These?
              </h2>
              <p className="text-rnb-gray mb-8">Healthcare leaders reach out when they start noticing:</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Scheduling inconsistencies or staffing strain",
                  "Policies that are unclear or outdated",
                  "Administrative workflows feel disorganized",
                  "Rapid growth creating operational pressure",
                  "Upcoming accreditation or regulatory review",
                  "New service lines or locations launching",
                  "Leadership stretched thin on coordination",
                  "Poor visibility into workforce costs",
                ].map((item, i) => (
                  <div key={item.title || item.name || item.label || i} className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50/60 border border-amber-100/60">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span className="text-sm text-rnb-navy">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 bg-rnb-navy/[0.03] rounded-xl border border-rnb-navy/10">
                <p className="text-sm text-rnb-navy font-medium">If even one of these feels familiar, a 30-minute conversation can help clarify your next steps.</p>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-rnb-teal mt-2 hover:underline">
                  Schedule a free consultation <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </AnimateOnScroll>
            <AnimateOnScroll direction="right">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-rnb-teal/10 to-rnb-blue/10 rounded-3xl blur-xl" />
                <img src={IMG_TEAM} alt="Healthcare compliance team" className="relative rounded-2xl shadow-2xl w-full object-cover" />
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* ═══ SERVICES. Outcome-Focused ═══ */}
      <section id="audit-services" className="py-16 sm:py-20 bg-[#f7f9fc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimateOnScroll>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-rnb-teal font-semibold text-sm uppercase tracking-wider">Audit Services</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mt-2 mb-4">
                Five Audits. One Goal: Operational Clarity.
              </h2>
              <p className="text-rnb-gray">Each engagement is tailored to your organization&apos;s size, stage, and specific challenges.</p>
            </div>
          </AnimateOnScroll>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {auditServices.map((service, i) => (
              <AnimateOnScroll key={service.title || service.name || service.label || i} delay={i * 0.07}>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden h-full flex flex-col group">
                  <div className={`h-1 bg-gradient-to-r ${service.gradient}`} />
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-sm`}>
                        <service.icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-heading font-bold text-rnb-navy text-sm leading-tight">{service.title}</h3>
                    </div>
                    <p className="text-sm font-semibold text-rnb-teal mb-4 italic">&ldquo;{service.outcome}&rdquo;</p>
                    <ul className="space-y-2 mt-auto">
                      {service.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2 text-xs text-gray-500">
                          <CheckCircle className="w-3.5 h-3.5 text-rnb-teal shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
          <AnimateOnScroll delay={0.4}>
            <div className="text-center mt-10">
              <Button onClick={scrollToForm} className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full gap-2 px-8 h-12 text-base font-semibold shadow-lg shadow-rnb-teal/20" data-testid="services-cta">
                Find Out Which Audit Fits Your Organization <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* ═══ PROCESS. Timeline ═══ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimateOnScroll>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="text-rnb-teal font-semibold text-sm uppercase tracking-wider">How It Works</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mt-2 mb-4">
                From First Call to Actionable Results in 4 Steps
              </h2>
            </div>
          </AnimateOnScroll>
          <div className="relative">
            {/* Vertical connector line */}
            <div className="absolute left-6 sm:left-8 top-8 bottom-8 w-px bg-gradient-to-b from-rnb-teal via-rnb-blue to-rnb-navy hidden sm:block" />
            <div className="space-y-6 sm:space-y-0">
              {[
                { step: "1", title: "Discovery & Scope Alignment", desc: "A focused call to understand your priorities, growth stage, and what's keeping leadership up at night.", time: "30 min call", icon: CalendarCheck },
                { step: "2", title: "Operational Assessment", desc: "We review documentation, workflows, staffing structures, and coordination processes. on your schedule.", time: "2–4 weeks", icon: FileSearch },
                { step: "3", title: "Findings & Recommendations", desc: "You receive a written summary of risks, prioritized fixes, and a clear sequencing plan your team can act on immediately.", time: "Delivered in days", icon: FileText },
                { step: "4", title: "Implementation Support (Optional)", desc: "Need hands-on help? We can support process redesign, training coordination, and policy refinement.", time: "Ongoing", icon: Wrench },
              ].map((s, i) => (
                <AnimateOnScroll key={s.key || s.name || s.title || i} delay={i * 0.1}>
                  <div className="flex gap-4 sm:gap-6 relative sm:py-6">
                    <div className="relative z-10 w-12 sm:w-16 shrink-0 flex flex-col items-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-rnb-navy to-rnb-blue flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-rnb-navy/20">
                        {s.step}
                      </div>
                    </div>
                    <div className="flex-1 bg-[#f7f9fc] rounded-xl p-5 sm:p-6 border border-gray-100">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h3 className="font-heading font-bold text-rnb-navy text-base flex items-center gap-2">
                          <s.icon className="w-4 h-4 text-rnb-teal" />
                          {s.title}
                        </h3>
                        <span className="text-[11px] font-medium bg-rnb-teal/10 text-rnb-teal px-2.5 py-1 rounded-full">{s.time}</span>
                      </div>
                      <p className="text-sm text-rnb-gray leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </AnimateOnScroll>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF / WHAT CLIENTS EXPECT ═══ */}
      <section className="py-16 relative overflow-hidden bg-rnb-navy">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-20 w-60 h-60 bg-rnb-teal/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 right-20 w-72 h-72 bg-rnb-blue/10 rounded-full blur-3xl animate-float" style={{ animationDelay: "2s" }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimateOnScroll direction="left">
              <span className="text-rnb-teal font-semibold text-sm uppercase tracking-wider">The RNB Difference</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-2 mb-8">
                What Every Client Receives
              </h2>
              <div className="space-y-4">
                {[
                  { icon: ClipboardCheck, text: "A structured, organized review process with clear milestones" },
                  { icon: Clock, text: "Defined timelines and proactive communication throughout" },
                  { icon: Zap, text: "Practical, actionable recommendations. not theoretical reports" },
                  { icon: FileText, text: "Deliverables leadership can use the day they're delivered" },
                  { icon: Users, text: "A collaborative approach focused on reducing your team's operational strain" },
                ].map((item, i) => (
                  <div key={item.title || item.name || item.label || i} className="flex items-start gap-4 bg-white/[0.05] backdrop-blur-sm rounded-xl p-4 border border-white/10">
                    <div className="w-9 h-9 rounded-lg bg-rnb-teal/15 flex items-center justify-center shrink-0">
                      <item.icon className="w-4 h-4 text-rnb-teal" />
                    </div>
                    <span className="text-sm text-white/80 leading-relaxed">{item.text}</span>
                  </div>
                ))}
              </div>
            </AnimateOnScroll>
            <AnimateOnScroll direction="right">
              <img src={IMG_WALKTHRU} alt="Healthcare audit walkthrough" className="rounded-2xl shadow-2xl w-full object-cover border border-white/10" />
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* ═══ FAQ. Objection Handling ═══ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimateOnScroll>
            <div className="text-center mb-12">
              <span className="text-rnb-teal font-semibold text-sm uppercase tracking-wider">Common Questions</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mt-2">
                Frequently Asked Questions
              </h2>
            </div>
          </AnimateOnScroll>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <AnimateOnScroll key={faq.question || faq.title || i} delay={i * 0.05}>
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50/50 transition-colors"
                    data-testid={`faq-${i}`}
                  >
                    <span className="font-semibold text-rnb-navy text-sm pr-4">{faq.q}</span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? "max-h-40 pb-4" : "max-h-0"}`}>
                    <p className="px-6 text-sm text-rnb-gray leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ LEAD CAPTURE FORM ═══ */}
      <section ref={formRef} id="audit-form" className="py-16 sm:py-20 bg-[#f7f9fc]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimateOnScroll>
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-rnb-navy to-rnb-blue px-6 sm:px-10 py-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-rnb-teal/10 rounded-full -translate-y-1/2 translate-x-1/3 blur-2xl" />
                <div className="relative">
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="form-heading">
                    Request Your Free Audit Overview
                  </h2>
                  <p className="text-white/60 text-sm">Tell us a bit about your organization and we&apos;ll send a tailored overview within 1–2 business days.</p>
                </div>
              </div>

              <div className="p-6 sm:p-10">
                {formSubmitted ? (
                  <div className="text-center py-8" data-testid="form-success">
                    <div className="w-16 h-16 bg-rnb-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-rnb-teal" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-rnb-navy mb-2">We&apos;ve Received Your Request</h3>
                    <p className="text-rnb-gray text-sm mb-6 max-w-md mx-auto">Check your email for a confirmation. A member of our team will reach out within 1–2 business days.</p>
                    <p className="text-sm text-gray-400">Want to talk sooner?{" "}
                      <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="text-rnb-teal font-semibold hover:underline">Book a call now</a>
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="audit-inquiry-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Full Name *</label>
                        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Jane Smith" required className="h-11" data-testid="form-name" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Work Email *</label>
                        <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="jane@organization.com" required className="h-11" data-testid="form-email" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Phone Number *</label>
                        <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(555) 123-4567" required className="h-11" data-testid="form-phone" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Organization *</label>
                        <Input value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} placeholder="Your healthcare organization" required className="h-11" data-testid="form-org" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Your Role</label>
                        <Input value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} placeholder="e.g. Practice Manager, COO" className="h-11" data-testid="form-role" />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1.5 block">What best describes the main challenge your organization is currently facing? *</label>
                      <select value={formData.main_challenge} onChange={(e) => setFormData({ ...formData, main_challenge: e.target.value })} required className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="form-main-challenge">
                        <option value="">Select one...</option>
                        <option value="Preparing for survey, accreditation, or compliance review">Preparing for survey, accreditation, or compliance review</option>
                        <option value="Staffing, scheduling, or workforce structure challenges">Staffing, scheduling, or workforce structure challenges</option>
                        <option value="Operational processes feel disorganized or unclear">Operational processes feel disorganized or unclear</option>
                        <option value="Opening a new location or launching a new service">Opening a new location or launching a new service</option>
                        <option value="Not sure. would like guidance">Not sure. would like guidance</option>
                      </select>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">How many employees are currently part of your organization? *</label>
                        <select value={formData.employee_count} onChange={(e) => setFormData({ ...formData, employee_count: e.target.value })} required className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="form-employee-count">
                          <option value="">Select one...</option>
                          <option value="1–10">1–10</option>
                          <option value="11–25">11–25</option>
                          <option value="26–50">26–50</option>
                          <option value="51–100">51–100</option>
                          <option value="101–200">101–200</option>
                          <option value="200+">200+</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">What is your expected timeline for addressing this need? *</label>
                        <select value={formData.timeline} onChange={(e) => setFormData({ ...formData, timeline: e.target.value })} required className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="form-timeline">
                          <option value="">Select one...</option>
                          <option value="Immediately / within 30 days">Immediately / within 30 days</option>
                          <option value="Within 1–3 months">Within 1–3 months</option>
                          <option value="Within 3–6 months">Within 3–6 months</option>
                          <option value="Just exploring options">Just exploring options</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1.5 block">Which best describes your current organizational structure? *</label>
                      <select value={formData.org_structure} onChange={(e) => setFormData({ ...formData, org_structure: e.target.value })} required className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="form-org-structure">
                        <option value="">Select one...</option>
                        <option value="Single location / early growth stage">Single location / early growth stage</option>
                        <option value="Multiple departments or service lines">Multiple departments or service lines</option>
                        <option value="Multiple locations">Multiple locations</option>
                        <option value="Preparing to launch or expand">Preparing to launch or expand</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1.5 block">Anything else you&apos;d like us to know?</label>
                      <textarea
                        value={formData.challenge}
                        onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
                        placeholder="Brief description helps us tailor the overview to your needs..."
                        rows={3}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid="form-challenge"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                      <Button type="submit" disabled={formSubmitting} size="lg" className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full px-8 h-12 text-base font-semibold shadow-lg shadow-rnb-teal/20 gap-2 w-full sm:w-auto" data-testid="form-submit">
                        {formSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> Get Your Free Overview</>}
                      </Button>
                      <div className="flex flex-wrap gap-3 text-[11px] text-gray-400">
                        <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> No spam, ever</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Response in 1–2 days</span>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </AnimateOnScroll>

          {/* Alternative CTA */}
          <AnimateOnScroll delay={0.2}>
            <div className="text-center mt-8">
              <p className="text-sm text-gray-400 mb-2">Prefer to talk first?</p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-rnb-teal font-semibold text-sm hover:underline" data-testid="prefer-call-link">
                <Phone className="w-4 h-4" /> Schedule a 30-minute consultation. no obligation
              </a>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* ═══ FINAL CTA BANNER ═══ */}
      <section className="py-16 sm:py-20 relative overflow-hidden bg-gradient-to-br from-rnb-navy via-[#1a3050] to-rnb-blue">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 bg-rnb-teal/10 rounded-full -translate-y-1/3 translate-x-1/4 blur-3xl" />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <AnimateOnScroll>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Don&apos;t Wait for the Next Survey to Find the Gaps
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
              The best time to audit your operations is before you&apos;re under pressure. Let&apos;s start with a conversation.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button onClick={scrollToForm} size="lg" className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full px-10 h-14 text-base font-semibold shadow-xl shadow-rnb-teal/30 gap-2.5 hover:-translate-y-0.5 transition-all" data-testid="final-cta-form">
                Get Your Free Audit Overview <ArrowRight className="w-5 h-5" />
              </Button>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="lg" className="text-white border-white/30 hover:bg-white/10 rounded-full px-8 h-14 text-base gap-2" data-testid="final-cta-call">
                  <Phone className="w-4 h-4" /> Book a Call Instead
                </Button>
              </a>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* ═══ STICKY MOBILE CTA ═══ */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-gray-200 px-4 py-3 transition-transform duration-300 lg:hidden ${showSticky ? "translate-y-0" : "translate-y-full"}`} data-testid="sticky-cta">
        <div className="flex gap-2 max-w-lg mx-auto">
          <Button onClick={scrollToForm} className="flex-1 bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full h-11 text-sm font-semibold gap-1.5" data-testid="sticky-cta-form">
            Request Free Overview <ArrowRight className="w-3.5 h-3.5" />
          </Button>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <Button variant="outline" className="rounded-full h-11 w-11 p-0" data-testid="sticky-cta-call">
              <Phone className="w-4 h-4" />
            </Button>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HealthcareOperationalAudits;
